"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function FoodStorageCalculatorPageClient() {
  const meta = TOOL_METADATA["food-storage-calculator"]

  const [people, setPeople] = useState("4")
  const [dietType, setDietType] = useState("balanced")
  const [shoppingFreq, setShoppingFreq] = useState("weekly")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numPeople = readCount(people, { min: 1, max: 20 })
    if (numPeople === null) return null

    let freshBase = numPeople * 3
    let frozenBase = numPeople * 1.5

    if (dietType === "vegetarian") {
      freshBase *= 1.3
      frozenBase *= 0.8
    } else if (dietType === "meat-heavy") {
      freshBase *= 0.9
      frozenBase *= 1.4
    }

    if (shoppingFreq === "weekly") {
      freshBase *= 1.2
      frozenBase *= 1.1
    } else if (shoppingFreq === "biweekly") {
      freshBase *= 1.5
      frozenBase *= 1.3
    } else if (shoppingFreq === "monthly") {
      freshBase *= 2
      frozenBase *= 1.8
    }

    const freshFood = Math.round(freshBase)
    const frozen = Math.round(frozenBase)
    const total = freshFood + frozen

    let recommendation: string
    if (total < 18) recommendation = "Compact refrigerator (14–18 cu ft)"
    else if (total < 25) recommendation = "Standard refrigerator (20–25 cu ft)"
    else if (total < 30) recommendation = "Large refrigerator (25–30 cu ft)"
    else recommendation = "Extra-large refrigerator (30+ cu ft)"

    return { freshFood, frozen, total, recommendation }
  }, [people, dietType, shoppingFreq])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="How much fresh and frozen"
      heroTitleItalic="space"
      heroTitlePost="do you really need?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~25 sec", "USDA storage guidelines"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "3", label: "Cu ft fresh / person", note: "USDA base" },
            { value: "1.5", label: "Cu ft frozen / person", note: "USDA base" },
            { value: "70/30", label: "Typical fresh:frozen", note: "Most homes" },
            { value: "+30%", label: "Vegetarian fresh", note: "Adjustment" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "food-storage-calculator")}
      cta={{
        title: "Match your storage profile to a real fridge.",
        body: "Filter by fresh and frozen capacity. Every product page lists exact compartment cubic-feet from the manufacturer spec sheet.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Diet matters
                <br />
                more than you think.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Vegetarian households need 30% more fresh space; meat-heavy households need 40% more freezer.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    People in household
                  </label>
                  <select
                    value={people}
                    onChange={(e) => setPeople(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="1">1 person</option>
                    <option value="2">2 people</option>
                    <option value="3">3 people</option>
                    <option value="4">4 people</option>
                    <option value="5">5 people</option>
                    <option value="6">6+ people</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Diet type
                  </label>
                  <select
                    value={dietType}
                    onChange={(e) => setDietType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="balanced">Balanced</option>
                    <option value="vegetarian">Vegetarian / fresh-heavy</option>
                    <option value="meat-heavy">Meat-heavy / frozen-heavy</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Shopping frequency
                  </label>
                  <select
                    value={shoppingFreq}
                    onChange={(e) => setShoppingFreq(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="daily">Daily / as needed</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Monthly bulk</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Monthly bulk shoppers need ~2× the fresh storage.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Total storage required
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.total ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">cu ft</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendation ?? "Enter household details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Fresh</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.freshFood ?? "—"} cu ft</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Frozen</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.frozen ?? "—"} cu ft</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Match your capacity"
          title="French door fridges with flexible compartments"
          description="The most popular style for households needing 22–28 cu ft total."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">USDA</em> storage. Real diet adjustments.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Base storage allocations come from USDA food storage guidance, with diet and shopping multipliers calibrated against household survey data.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Per-person base",
                  d: "3 cu ft fresh, 1.5 cu ft frozen per household member. Maps to USDA refrigerator/freezer storage guidance.",
                },
                {
                  n: "02",
                  t: "Diet adjustment",
                  d: "Vegetarian: +30% fresh, -20% frozen. Meat-heavy: -10% fresh, +40% frozen.",
                },
                {
                  n: "03",
                  t: "Shopping multiplier",
                  d: "Weekly +20% fresh / +10% frozen; biweekly +50% / +30%; monthly +100% / +80%.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges for fresh-food-first kitchens"
          description="Eye-level fresh storage, drawer-access freezer below — ideal for vegetarian households."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
